// src/scenes/machineFs/MachineFsList.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  TextField,
  useTheme,
} from "@mui/material";
import SimpleTable from "../../components/SimpleTable";
import axios from "axios";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err?.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err?.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err?.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function MachineFsList() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // user info
  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || ""
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw
    );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? ""
  );
  // const canEdit = !isSalesMan;


  // table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  // dialogs
  const [detailRow, setDetailRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({ fsNo: "", machineNo: "" });
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ---------- fetch data ---------- */
  const load = useCallback(async () => {
    setLoading(true);

    let apiUrl = "/api/machinfs";
    if (isSalesMan) {
      apiUrl = `/api/machinfs/user/${currentUserId}`;
    }

    const { data, error } = await getJSON(apiUrl);
    setLoading(false);

    if (error) {
      setToast({ open: true, msg: "Failed to load FS records", sev: "error" });
      return;
    }

    const rowsArray = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
      ? data
      : [];

    setRows(
      rowsArray.map((r) => ({
        ...r,
        fsNo: r.fsNo ?? "-",
        machineNo: r.machineNo ?? "-",
        userName: r?.User?.fullName ?? "-",
        createdAt: r.createdAt ?? "-",
      }))
    );
  }, [currentUserId, isSalesMan]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- table columns ---------- */
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const pm = params.api?.state?.pagination?.paginationModel || { page: 0, pageSize: rows.length || 0 };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "fsNo", headerName: "FS No.", width: 130 },
    { field: "machineNo", headerName: "Machine No.", width: 130 },
    { field: "userName", headerName: "User", flex: 1 },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      valueGetter: (params) =>
        params.row?.createdAt ? new Date(params.row.createdAt).toLocaleString() : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(row);
                  setEditValues({ fsNo: row.fsNo || "", machineNo: row.machineNo || "" });
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
    "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
  };

  /* ---------- edit handler ---------- */
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    const payload = {
      fsNo: String(editValues.fsNo ?? "").trim(),
      machineNo: String(editValues.machineNo ?? "").trim(),
    };

    const { data, error } = await putJSON(`/api/machinfs/${editRow.id}`, payload);
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    setToast({ open: true, msg: data?.message || "FS updated successfully", sev: "success" });
    setEditOpen(false);
    setEditRow(null);
    load();
  };

  /* ---------- delete handler ---------- */
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/machinfs/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setToast({ open: true, msg: data?.message || "FS deleted successfully", sev: "success" });
    setDeleteRow(null);
    load();
  };

  return (
    <Box m="20px">
      <Typography variant="h5" mb={2} fontWeight="bold">
        Machine FS Records
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SimpleTable
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          height="75vh"
          pageSizeOptions={[5, 10, 25]}
          sx={gridSx}
        />
      )}

    

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit FS</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="FS No."
              value={editValues.fsNo}
              onChange={(e) => setEditValues((s) => ({ ...s, fsNo: e.target.value }))}
              fullWidth
              variant="filled"
            />
            <TextField
              label="Machine No."
              value={editValues.machineNo}
              onChange={(e) => setEditValues((s) => ({ ...s, machineNo: e.target.value }))}
              fullWidth
              variant="filled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={saving} variant="contained" color="secondary">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete FS</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete this FS record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} color="error" variant="contained">
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ---------- subcomponent ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>{value ?? "-"}</Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
